import React, { useEffect } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import Button from '../../common/Button';
import { type TypeScenario } from '../../common/dataTypes';
import {
  useCreateScenario,
  useUpdateScenarioById,
} from '../../hooks/tanstack/useScenariosHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import FormInput from '../form/FormInput';

type CreateScenarioFormDialogProps = {
  open: boolean;
  title: string;
  showTrigger?: boolean;
  trigger?: {
    label: React.ReactNode;
    style?: React.CSSProperties;
    onClick?: () => void;
  };
  defaultValues?: {
    id: number;
    name: string;
    description?: string;
  };
  dataTableName?: string;
  dataTableDisplayName?: string;
  handleCloseDialog: () => void;
  handleSubmitDialog?: (data: TypeScenario) => void;
  handleOpenDialog?: () => void;
};

const CreateScenarioSchema = z.object({
  name: z.string().trim(),
  description: z.string().trim().optional(),
  dataTable: z.string().trim(),
});

type CreateScenarioSchemaType = z.infer<typeof CreateScenarioSchema>;

const CreateScenarioFormDialog = ({
  open,
  title,
  trigger,
  showTrigger = true,
  defaultValues,
  handleOpenDialog,
  dataTableName,
  dataTableDisplayName,
  handleCloseDialog,
  handleSubmitDialog,
}: CreateScenarioFormDialogProps) => {
  const form = useForm<CreateScenarioSchemaType>({
    resolver: zodResolver(CreateScenarioSchema),
  });

  const { handleSubmit, reset } = form;
  const { projectId, inputTableName, orgId, modelId } = useGetRouteParams();

  const data_table_name = dataTableName || inputTableName;

  const { mutate: createScenario, isPending: isCreatingScenario } =
    useCreateScenario({
      onSuccess: (data) => {
        reset();
        handleCloseDialog();
        handleSubmitDialog?.(data);
      },
    });

  const { mutate: updateScenario, isPending: isUpdatingScenario } =
    useUpdateScenarioById({
      onSuccess: () => {
        reset();
        handleCloseDialog();
      },
    });

  useEffect(() => {
    if (data_table_name) {
      reset({
        ...defaultValues,
        dataTable: data_table_name,
      });
    }
  }, [defaultValues, data_table_name]);

  const onSubmit: SubmitHandler<CreateScenarioSchemaType> = (data) => {
    if (!data_table_name) {
      return;
    }

    if (defaultValues) {
      updateScenario({
        orgId,
        projectId,
        modelId,
        id: defaultValues.id,
        data: {
          name: data.name,
          description: data.description,
          data_table_name,
          model: modelId,
        },
      });
      return;
    }

    createScenario({
      orgId,
      projectId,
      modelId,
      data: {
        name: data.name,
        description: data.description,
        data_table_name,
        model: modelId,
      },
    });
  };

  return (
    <>
      {showTrigger && (
        <button
          onClick={handleOpenDialog}
          className="text-start"
          type="button"
          style={trigger?.style}
        >
          {trigger ? (
            trigger.label
          ) : (
            <Plus width={20} height={20} color="#E8F2FF" />
          )}
        </button>
      )}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle className="w-full flex-col px-2">
          <h2 className="pt-2 text-2xl font-bold">{title}</h2>
          <p className=" text-[#666] font-normal">
            Table:{' '}
            <span className="font-bold text-black">
              {dataTableDisplayName ?? ''}
            </span>
          </p>
        </DialogTitle>
        <DialogContent>
          <div className="z-50">
            <FormProvider {...form}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="mb-2 space-y-6"
              >
                <div className="space-y-4">
                  <FormInput label="Scenario Name" name="name" required />
                  <FormInput
                    label="Description"
                    name="description"
                    multiline
                    rows={4}
                    placeholder="(Optional)"
                  />
                </div>
                <div className="flex-end flex justify-end gap-3">
                  <Button onClick={handleCloseDialog} variant="outlined">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isCreatingScenario || isUpdatingScenario}
                    variant="contained_blue"
                  >
                    {defaultValues ? 'Save' : 'Create'}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateScenarioFormDialog;
